/**
 * tutorStream — pure parser/reducer for Tutor::ChatRunner's SSE turn stream
 * (moved from the web client's `pages/reader/tutorStream.ts`, TASK-019 Phase
 * 4 "Package condivisi — estrazione tutor", so mobile can reuse the exact
 * same event types/parser/reducer without duplicating them by hand).
 *
 * The transport (`streamTutorMessage`/`StreamTutorMessageArgs`, built on
 * `@microsoft/fetch-event-source`) stays in the web app: it is DOM-coupled
 * and mobile uses its own `expo/fetch`-based transport. Everything here is
 * pure, framework-free TypeScript:
 *   - `parseTutorEvent`: raw SSE `data:` payload -> typed event, or null for
 *     anything malformed (never throws on bad input).
 *   - `applyTutorEvent`: folds one event onto a `TutorTurnState` accumulator.
 */

// ---------------------------------------------------------------------------
// Event types — mirrors TASK-010's "Stream SSE" API Contract exactly, as
// actually emitted by Tutor::ChatRunner#emit_event.
// ---------------------------------------------------------------------------

export interface TutorTextEvent {
  type: 'text'
  delta: string
}

/**
 * Live citation event. `sub_para_id`/`block_id` are the fine-grained anchor
 * (same shape as the persisted `TutorCitation`, `./types.ts`) — the backend
 * emits them on the wire too, but as optional/nullable: older events (and
 * any hit that couldn't resolve a fine anchor) may omit them, in which case
 * navigation falls back to section-level.
 *
 * `title` (TASK-012 2026-07-08 "citazioni server-owned" revision) — the
 * sub-theme title `Tutor::CitationBuilder` resolved from the manual's toc,
 * always a string (never undefined/null on the wire — the backend falls
 * back to `""` when nothing in the toc resolves). Parsed the same
 * null-safe way as the rest of this event: a non-string/missing value never
 * crashes parsing, it just becomes `''`.
 */
export interface TutorCitationEvent {
  type: 'citation'
  section_id: string
  sub_para_id: string | null
  block_id: string | null
  title: string
  quote: string
  char_start: number
  char_end: number
}

export interface TutorLaunchQuizAction {
  type: 'action'
  name: 'launch_quiz'
  quiz_session_id: string
}

export interface TutorOpenSectionAction {
  type: 'action'
  name: 'open_section'
  section_id: string
}

export type TutorActionEvent = TutorLaunchQuizAction | TutorOpenSectionAction

/**
 * The 5 tools `Tutor::Toolbox` dispatches (TASK-012 Phase B,
 * app/services/tutor/toolbox/tools/*.rb `tool_name`) — kept as an explicit
 * whitelist (not `string`) so an unrecognised name from the wire is rejected
 * by `parseTutorEvent` rather than silently rendered, same posture as the
 * `action` event's `name` check just above.
 */
export const TOOL_STEP_NAMES = [
  'search_manual',
  'get_quiz_results',
  'get_mastery',
  'launch_quiz',
  'open_section',
] as const

export type ToolStepName = (typeof TOOL_STEP_NAMES)[number]

/**
 * `Tutor::AgenticRunner` (app/services/tutor/agentic_runner.rb) emits one
 * `phase:"start"` right before `Toolbox#execute` and one `phase:"done"`
 * right after, for every tool call in the loop — one at a time (the runner
 * executes each turn's function_calls sequentially, never concurrently), so
 * at most one tool is ever "in progress" for a given turn. Lets the UI show
 * "sta cercando nel manuale..." while the model is waiting on a tool instead
 * of a blank gap between text deltas.
 */
export interface TutorToolStepEvent {
  type: 'tool_step'
  name: ToolStepName
  phase: 'start' | 'done'
}

export interface TutorDoneEvent {
  type: 'done'
  token_usage: Record<string, number>
}

export interface TutorLimitReachedEvent {
  type: 'limit_reached'
  resets_at: string
}

export interface TutorErrorEvent {
  type: 'error'
  message: string
}

export type TutorStreamEvent =
  | TutorTextEvent
  | TutorCitationEvent
  | TutorActionEvent
  | TutorToolStepEvent
  | TutorDoneEvent
  | TutorLimitReachedEvent
  | TutorErrorEvent

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/** Parses one SSE `data:` payload. Returns null for anything malformed or
 *  unrecognised — callers should skip, not crash a whole turn on one bad
 *  frame. */
export function parseTutorEvent(raw: string): TutorStreamEvent | null {
  let data: unknown
  try {
    data = JSON.parse(raw)
  } catch {
    return null
  }
  if (!isRecord(data) || typeof data['type'] !== 'string') return null

  switch (data['type']) {
    case 'text':
      return typeof data['delta'] === 'string' ? { type: 'text', delta: data['delta'] } : null

    case 'citation':
      return typeof data['section_id'] === 'string' &&
        typeof data['quote'] === 'string' &&
        typeof data['char_start'] === 'number' &&
        typeof data['char_end'] === 'number'
        ? {
            type: 'citation',
            section_id: data['section_id'],
            // Optional/nullable fine anchor — absent on older events or a hit
            // that couldn't resolve one; never crash, just fall back to null.
            sub_para_id: typeof data['sub_para_id'] === 'string' ? data['sub_para_id'] : null,
            block_id: typeof data['block_id'] === 'string' ? data['block_id'] : null,
            title: typeof data['title'] === 'string' ? data['title'] : '',
            quote: data['quote'],
            char_start: data['char_start'],
            char_end: data['char_end'],
          }
        : null

    case 'action':
      if (data['name'] === 'launch_quiz' && typeof data['quiz_session_id'] === 'string') {
        return { type: 'action', name: 'launch_quiz', quiz_session_id: data['quiz_session_id'] }
      }
      if (data['name'] === 'open_section' && typeof data['section_id'] === 'string') {
        return { type: 'action', name: 'open_section', section_id: data['section_id'] }
      }
      return null

    case 'tool_step':
      return typeof data['name'] === 'string' &&
        (TOOL_STEP_NAMES as readonly string[]).includes(data['name']) &&
        (data['phase'] === 'start' || data['phase'] === 'done')
        ? { type: 'tool_step', name: data['name'] as ToolStepName, phase: data['phase'] }
        : null

    case 'done':
      return {
        type: 'done',
        token_usage: isRecord(data['token_usage'])
          ? (data['token_usage'] as Record<string, number>)
          : {},
      }

    case 'limit_reached':
      return typeof data['resets_at'] === 'string'
        ? { type: 'limit_reached', resets_at: data['resets_at'] }
        : null

    case 'error':
      return typeof data['message'] === 'string'
        ? { type: 'error', message: data['message'] }
        : null

    default:
      return null
  }
}

// ---------------------------------------------------------------------------
// applyTutorEvent — pure reducer, one turn's worth of accumulated state.
// ---------------------------------------------------------------------------

export interface TutorTurnState {
  text: string
  citations: TutorCitationEvent[]
  action: TutorActionEvent | null
  /**
   * The tool currently between its `tool_step` `start` and `done` (null when
   * none is in flight) — a `start` with no `done` yet means "in progress",
   * which is all the UI needs to show a "sta cercando..." indicator. At most
   * one is ever set at a time (see `TutorToolStepEvent` doc).
   */
  activeToolStep: ToolStepName | null
  done: boolean
  tokenUsage: Record<string, number> | null
  /** `resets_at` (ISO8601) from a `limit_reached` event, if any. */
  limitReachedAt: string | null
  error: string | null
}

export const EMPTY_TURN_STATE: TutorTurnState = {
  text: '',
  citations: [],
  action: null,
  activeToolStep: null,
  done: false,
  tokenUsage: null,
  limitReachedAt: null,
  error: null,
}

export function applyTutorEvent(state: TutorTurnState, event: TutorStreamEvent): TutorTurnState {
  switch (event.type) {
    case 'text':
      return { ...state, text: state.text + event.delta }
    case 'citation':
      return { ...state, citations: [...state.citations, event] }
    case 'action':
      return { ...state, action: event }
    case 'tool_step':
      if (event.phase === 'start') return { ...state, activeToolStep: event.name }
      // phase 'done' — only clear if it matches what's tracked as active (the
      // runner always pairs start/done for the same tool sequentially, so
      // this is just defensive against an out-of-order/duplicate frame).
      return state.activeToolStep === event.name ? { ...state, activeToolStep: null } : state
    case 'done':
      return { ...state, done: true, tokenUsage: event.token_usage, activeToolStep: null }
    case 'limit_reached':
      return { ...state, done: true, limitReachedAt: event.resets_at, activeToolStep: null }
    case 'error':
      return { ...state, done: true, error: event.message, activeToolStep: null }
    default:
      return state
  }
}
